import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutService } from '@layout/service/app.layout.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom, interval, Subscription } from 'rxjs';
import { passwordMatchingValidator } from '@core/utils/password-matching-validator';
import { passwordStrengthValidator } from '@core/utils/password-strength.validator';
import { AuthService } from '@core/services/auth/auth.service';
import { ResetPassword } from '@modules/auth/models/reset-password';
import { ToastService } from '@layout/service/toast.service';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'cwa-reset-password',
  templateUrl: 'reset-password.component.html',
  standalone: false,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private layoutService = inject(LayoutService);
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private titleService = inject(Title);

  token: string = '';
  formGroup!: FormGroup;
  showErrorMessage = false;
  loading = false;
  verifying = true;
  showSuccessScreen = false;
  countdown = 5; // seconds until auto-redirect after successful reset
  private countdownSubscription?: Subscription;

  control = (name: string): FormControl => this.formGroup.get(name) as FormControl;

  constructor() {
    this.titleService.setTitle('Auth - Reset Password | Cogwheel Analytics Tools');
  }

  async ngOnInit(): Promise<void> {
    // Get token from query params
    this.token = this.route.snapshot.queryParams['token'] || '';

    if (!this.token) {
      this.verifying = false;
      this.toast.showError('No reset token provided', '');
      await this.router.navigate(['/login']);
      return;
    }

    // Verify token before showing form
    try {
      await lastValueFrom(this.authService.verifyResetPasswordToken(this.token));
      this.verifying = false;

      // Initialize form after successful verification
      this.formGroup = this.fb.group(
        {
          password: ['', [Validators.required, passwordStrengthValidator()]],
          confirm_password: ['', [Validators.required]],
        },
        { validators: passwordMatchingValidator() }
      );
    } catch {
      this.verifying = false;
      this.toast.showError('Invalid or expired reset token', '');
      await this.router.navigate(['/login']);
    }
  }

  get dark(): boolean {
    return this.layoutService.config.colorScheme !== 'light';
  }

  ngOnDestroy(): void {
    // Clean up countdown subscription if exists
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
  }

  async resetPassword(): Promise<void> {
    if (this.formGroup.invalid) return;
    try {
      this.loading = true;
      await lastValueFrom(
        this.authService.resetPassword(this.token, this.formGroup.value as ResetPassword)
      );
      this.loading = false;
      this.showErrorMessage = false;

      // Show success screen instead of immediate redirect
      this.showSuccessScreen = true;

      // Start countdown for auto-redirect
      this.startCountdown();
    } catch {
      this.loading = false;
      this.showErrorMessage = true;
    }
  }

  private startCountdown(): void {
    this.countdown = 5; // reset countdown
    this.countdownSubscription = interval(1000).subscribe(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.navigateToLogin();
      }
    });
  }

  navigateToLogin(): void {
    // Clean up subscription before navigating
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
    this.router.navigate(['/login']);
  }
}
